import { useState } from 'react';
import type { ReactNode } from 'react';
import { useCreateTag, useDeleteTag, useRenameTag, useSetTagIcon, useTags } from '../api/queries';
import { ApiError } from '../api/client';
import { CategoryIcon } from '../components/CategoryIcon';
import { Dialog, dialogStyles } from '../components/Dialog';
import { IconPicker } from '../components/IconPicker';
import ui from '../components/ui.module.css';
import styles from './Categories.module.css';

/**
 * The categories, and everything an administrator may do to one: create it,
 * rename it, give it an icon, delete it (02-requirements.md §Category
 * Administration).
 *
 * **Create and rename arrived 2026-08-17** (user request). Until then a category
 * could only be born by being typed onto a poll — the reasoning being that a tag
 * with no poll is a name nobody chose — but that rule never held: `setPollTags`
 * drops the join rows and leaves the tag, so detached categories were always
 * reachable, which is why this screen already had a delete for them. A rename
 * reaches every poll carrying the category, including the primary category the
 * app prints, and that is the API's work and not this screen's.
 */

/** create and rename are the same form; only the API call differs. */
function NameDialog({
  title,
  initial,
  action,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  title: string;
  initial: string;
  action: string;
  pending: boolean;
  error: string | null;
  onSubmit: (name: string) => void;
  onClose: () => void;
}): ReactNode {
  const [name, setName] = useState(initial);
  // Trimmed for the check as well as for the send: the API refuses a name that is
  // blank once trimmed, and a button that looks enabled for "   " would only
  // trade a local check for a round trip.
  const clean = name.trim();

  return (
    <Dialog title={title} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (clean.length >= 2) onSubmit(clean);
        }}
      >
        <div className={ui.field}>
          <label className={ui.label} htmlFor="tag-name">
            Nome
          </label>
          <input
            id="tag-name"
            className={ui.input}
            value={name}
            autoFocus
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        {error ? <p className={ui.error}>{error}</p> : null}

        <div className={dialogStyles.actions}>
          <button
            type="submit"
            className={ui.primary}
            disabled={pending || clean.length < 2 || clean === initial.trim()}
          >
            {pending ? 'Salvando…' : action}
          </button>
          <button type="button" className={ui.ghost} onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** The one message both writes need, since both can collide on a name. */
function nameFailure(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.code === 'tag_exists') {
    const clash = error.details?.tag as { name: string } | undefined;
    return clash
      ? `Já existe uma categoria chamada "${clash.name}". Nomes iguais a menos de maiúsculas e acentuação contam como o mesmo.`
      : 'Já existe uma categoria com esse nome.';
  }
  if (error instanceof ApiError && error.code === 'invalid_name') {
    return 'Escreva um nome com pelo menos duas letras.';
  }
  return 'Não foi possível salvar a categoria.';
}

export function Categories(): ReactNode {
  const { data: tags, isLoading, error } = useTags();
  const setIcon = useSetTagIcon();
  const removeTag = useDeleteTag();
  const addTag = useCreateTag();
  const rename = useRenameTag();
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const chosen = (tags ?? []).find((tag) => tag.id === editing) ?? null;
  const doomed = (tags ?? []).find((tag) => tag.id === deleting) ?? null;
  const target = (tags ?? []).find((tag) => tag.id === renaming) ?? null;

  // The API refuses when some poll carries this category and nothing else, and
  // names those polls — showing them is the difference between "não deu" and a
  // list of what to fix.
  const blocking =
    removeTag.error instanceof ApiError && removeTag.error.code === 'tag_last_on_poll'
      ? ((removeTag.error.details?.polls as { id: string; title: string }[] | undefined) ?? [])
      : null;

  const deleteFailure = blocking
    ? null
    : removeTag.error
      ? 'Não foi possível excluir a categoria.'
      : null;

  const save = (icon: string | null): void => {
    if (!chosen) return;
    setIcon.mutate(
      { id: chosen.id, icon },
      // Closed only on success: a failed write that closes the dialog looks
      // exactly like a successful one until the list fails to change.
      { onSuccess: () => setEditing(null) },
    );
  };

  const failure =
    setIcon.error instanceof ApiError && setIcon.error.code === 'unknown_icon'
      ? 'Este ícone não existe nesta versão do aplicativo.'
      : setIcon.error
        ? 'Não foi possível salvar o ícone.'
        : null;

  return (
    <section className={ui.panel}>
      <div className={ui.spread}>
        <h1 className={ui.h1}>Categorias</h1>
        <button
          type="button"
          className={ui.primary}
          onClick={() => {
            addTag.reset();
            setCreating(true);
          }}
        >
          Nova categoria
        </button>
      </div>

      <p className={ui.muted}>
        O ícone aparece na grade de categorias do aplicativo. Uma categoria também nasce ao ser
        escrita numa enquete — criar aqui serve para preparar as categorias antes das enquetes.
        Renomear troca o nome em todas as enquetes que a usam. Excluir tira a categoria de todas
        elas, e não é possível quando alguma enquete usa só ela.
      </p>

      {isLoading ? <p className={ui.muted}>Carregando…</p> : null}
      {error ? <p className={ui.error}>Não foi possível carregar as categorias.</p> : null}

      {tags && tags.length === 0 ? (
        <p className={ui.muted}>
          Nenhuma categoria ainda. Crie uma aqui, ou escreva uma ao criar ou editar uma enquete.
        </p>
      ) : null}

      {tags && tags.length > 0 ? (
        <ul className={styles.list} aria-label="Categorias">
          {tags.map((tag) => (
            <li key={tag.id} className={styles.row}>
              <span className={styles.art}>
                {tag.icon ? (
                  <CategoryIcon name={tag.icon} size="row" />
                ) : (
                  <span className={ui.muted}>—</span>
                )}
              </span>
              <span className={styles.name}>{tag.name}</span>
              {/* Both counts, because they answer different questions: how many
                  polls carry the category at all, and how many the app's grid
                  will actually list under it (§Category Browsing). */}
              <span className={ui.muted}>
                {tag.active_poll_count} aberta{tag.active_poll_count === 1 ? '' : 's'} de{' '}
                {tag.poll_count}
              </span>
              <button
                type="button"
                className={`${ui.ghost} ${ui.small}`}
                aria-label={`Renomear a categoria ${tag.name}`}
                onClick={() => {
                  rename.reset();
                  setRenaming(tag.id);
                }}
              >
                Renomear
              </button>
              <button
                type="button"
                className={`${ui.ghost} ${ui.small}`}
                onClick={() => {
                  setIcon.reset();
                  setEditing(tag.id);
                }}
              >
                {tag.icon ? 'Alterar ícone' : 'Escolher ícone'}
              </button>
              <button
                type="button"
                className={`${ui.danger} ${ui.small}`}
                aria-label={`Excluir a categoria ${tag.name}`}
                onClick={() => {
                  removeTag.reset();
                  setDeleting(tag.id);
                }}
              >
                Excluir
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {creating ? (
        <NameDialog
          title="Nova categoria"
          initial=""
          action="Criar categoria"
          pending={addTag.isPending}
          error={nameFailure(addTag.error)}
          onSubmit={(name) =>
            // The icon is a separate gesture: the row's own button opens the
            // picker, which is where an administrator can see what they are choosing.
            addTag.mutate({ name }, { onSuccess: () => setCreating(false) })
          }
          onClose={() => setCreating(false)}
        />
      ) : null}

      {target ? (
        <NameDialog
          title={`Renomear ${target.name}`}
          initial={target.name}
          action="Salvar nome"
          pending={rename.isPending}
          error={nameFailure(rename.error)}
          onSubmit={(name) =>
            rename.mutate({ id: target.id, name }, { onSuccess: () => setRenaming(null) })
          }
          onClose={() => setRenaming(null)}
        />
      ) : null}

      {doomed ? (
        <Dialog title={`Excluir ${doomed.name}?`} onClose={() => setDeleting(null)}>
          {blocking ? (
            <>
              <p className={ui.error}>
                Não dá para excluir: {blocking.length === 1 ? 'a enquete abaixo usa' : 'as enquetes abaixo usam'}{' '}
                só esta categoria. Toda enquete precisa de pelo menos uma — dê outra categoria a{' '}
                {blocking.length === 1 ? 'ela' : 'elas'} antes.
              </p>
              <ul>
                {blocking.map((poll) => (
                  <li key={poll.id}>{poll.title}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className={ui.muted}>
              {doomed.poll_count === 0
                ? 'Nenhuma enquete usa esta categoria. Excluir não muda nada além de tirá-la das listas.'
                : `A categoria sai de ${doomed.poll_count} enquete${doomed.poll_count === 1 ? '' : 's'}, que continuam existindo com as outras categorias delas. Não dá para desfazer.`}
            </p>
          )}

          {deleteFailure ? <p className={ui.error}>{deleteFailure}</p> : null}

          <div className={dialogStyles.actions}>
            <button
              type="button"
              className={ui.danger}
              // Hidden rather than disabled once the API has refused: the button
              // would do the same thing again, and the answer is on screen.
              hidden={Boolean(blocking)}
              disabled={removeTag.isPending}
              onClick={() =>
                removeTag.mutate(doomed.id, { onSuccess: () => setDeleting(null) })
              }
            >
              {removeTag.isPending ? 'Excluindo…' : 'Excluir categoria'}
            </button>
            <button type="button" className={ui.ghost} onClick={() => setDeleting(null)}>
              {blocking ? 'Fechar' : 'Cancelar'}
            </button>
          </div>
        </Dialog>
      ) : null}

      {chosen ? (
        <IconPicker
          categoryName={chosen.name}
          current={chosen.icon}
          saving={setIcon.isPending}
          error={failure}
          onPick={save}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
